/**
 * ZoneFactory — Creates Fabric.js objects from template zone definitions.
 *
 * Each zone type maps to a factory method that produces one or more
 * Fabric objects positioned and styled according to the zone spec.
 *
 * Magazine spreads and covers are rendered at `renderScale`× the zone bounds
 * for high quality, then scaled back down via Fabric's scaleX/scaleY.
 * Source pages are pre-scaled using createImageBitmap with Lanczos resampling.
 */
import * as fabric from 'fabric';
import { SpreadRenderer3D } from '../spreads/SpreadRenderer3D.js';
import { CoverRenderer3D } from '../spreads/CoverRenderer3D.js';
import { SpreadRenderer } from '../spreads/SpreadRenderer.js';
import { CoverRenderer } from '../spreads/CoverRenderer.js';
import { loadImage } from '../utils/imageUtils.js';

/**
 * Check if WebGL is available in the current browser context.
 * @returns {boolean}
 */
function isWebGLAvailable() {
  try {
    const canvas = document.createElement('canvas');
    return !!(canvas.getContext('webgl') || canvas.getContext('experimental-webgl'));
  } catch {
    return false;
  }
}

export class ZoneFactory {
  /**
   * @param {object} [options]
   * @param {number} [options.renderScale=3] - Internal render multiplier for spread/cover quality
   */
  constructor(options = {}) {
    this.renderScale = options.renderScale ?? 3;
    // When draftMode is on (e.g. during a live orbit drag), magazine zones
    // render at draftScale for speed; the full-quality render runs on release.
    this.draftMode = false;
    this.draftScale = options.draftScale ?? 1;
    this._webglAvailable = isWebGLAvailable();

    if (this._webglAvailable) {
      this.spreadRenderer = new SpreadRenderer3D();
      this.coverRenderer = new CoverRenderer3D();
    } else {
      console.warn('WebGL not available — falling back to 2D renderers');
      this.spreadRenderer = new SpreadRenderer();
      this.coverRenderer = new CoverRenderer();
    }
  }

  /**
   * Create a Fabric object (or group) for a zone definition.
   * @param {object} zone - Zone definition from template JSON
   * @param {object} variables - Bound variable values
   * @returns {Promise<fabric.Object|null>}
   */
  async createZone(zone, variables = {}) {
    switch (zone.type) {
      case 'text':
        return this._createText(zone, variables);
      case 'magazineCover':
        return this._createMagazineCover(zone, variables);
      case 'magazineSpread':
        return this._createMagazineSpread(zone, variables);
      case 'badge':
        return this._createBadge(zone, variables);
      case 'cta':
        return this._createCTA(zone, variables);
      case 'image':
        return this._createImage(zone, variables);
      case 'rect':
        return this._createRect(zone, variables);
      default:
        console.warn(`Unknown zone type: ${zone.type}`);
        return null;
    }
  }

  /**
   * Create a text zone.
   */
  _createText(zone, variables) {
    const text = this._resolveBinding(zone.binding, variables) || zone.label || 'Text';
    const style = zone.style || {};

    const fabricText = new fabric.Text(text, {
      left: zone.bounds.x,
      top: zone.bounds.y,
      width: zone.bounds.width,
      originX: 'left',
      originY: 'top',
      fontFamily: style.fontFamily || 'Helvetica',
      fontSize: style.fontSize || 24,
      fontWeight: style.fontWeight || 400,
      fontStyle: style.fontStyle || 'normal',
      fill: style.fill || '#FFFFFF',
      textAlign: style.textAlign || 'left',
      selectable: true,
      evented: true,
    });

    // Apply shadow if defined
    if (style.shadow) {
      fabricText.shadow = this._parseShadowString(style.shadow);
    }

    // Apply text-specific properties
    fabricText.set('zoneId', zone.id);
    fabricText.set('zoneType', zone.type);

    return fabricText;
  }

  /**
   * Create a magazine cover zone — renders the cover with perspective and shadow
   * at high resolution, then scales down for display.
   */
  async _createMagazineCover(zone, variables) {
    const imageSource = this._resolveBinding(zone.binding, variables);
    if (!imageSource) {
      return this._createPlaceholder(zone, 'Cover');
    }

    try {
      const img = typeof imageSource === 'string' ? await loadImage(imageSource) : imageSource;
      const transform = zone.transform || {};
      const s = this.draftMode ? this.draftScale : this.renderScale;

      // Render to a SQUARE viewport so the (possibly rotated) magazine is framed
      // consistently and never squashed. The source keeps its natural aspect.
      const viewport = Math.round(Math.max(zone.bounds.width, zone.bounds.height) * s);
      const coverCanvas = await this._scaleToBudget(img, viewport);

      const renderOpts = this._webglAvailable
        ? {
            width: viewport,
            height: viewport,
            cameraTiltX: transform.cameraTiltX ?? 35,
            cameraRotateY: transform.cameraRotateY ?? -20,
            cameraRotateZ: transform.cameraRotateZ ?? 0,
            thickness: transform.thickness ?? 0.15,
            aspectRatio: transform.aspectRatio ?? null,
            zoom: transform.zoom ?? 1,
            panX: transform.panX ?? 0,
            panY: transform.panY ?? 0,
            roughness: transform.roughness ?? 0.3,
            shadowStrength: transform.shadowStrength ?? 0.25,
          }
        : {
            width: viewport,
            height: viewport,
            rotateY: transform.cameraRotateY ?? -20,
            shadow: true,
          };

      const renderedCanvas = this.coverRenderer.render(coverCanvas, renderOpts);

      // Contain-fit the square render into the bounds box, centered.
      const fabricImg = this._placeRenderInBounds(renderedCanvas, zone, viewport);
      fabricImg.set('zoneId', zone.id);
      fabricImg.set('zoneType', zone.type);
      return fabricImg;
    } catch (err) {
      console.error('Failed to render cover:', err);
      return this._createPlaceholder(zone, 'Cover');
    }
  }

  /**
   * Create a magazine spread zone — renders an open book spread
   * at high resolution, then scales down for display.
   */
  async _createMagazineSpread(zone, variables) {
    const spreadData = this._resolveBinding(zone.binding, variables);
    if (!spreadData || !spreadData.leftPage || !spreadData.rightPage) {
      return this._createPlaceholder(zone, 'Spread');
    }

    try {
      const leftImg = typeof spreadData.leftPage === 'string'
        ? await loadImage(spreadData.leftPage) : spreadData.leftPage;
      const rightImg = typeof spreadData.rightPage === 'string'
        ? await loadImage(spreadData.rightPage) : spreadData.rightPage;

      const transform = zone.transform || {};
      const s = this.draftMode ? this.draftScale : this.renderScale;

      // Render to a SQUARE viewport so the open spread is framed consistently
      // and never squashed. Each page keeps its natural aspect ratio.
      const viewport = Math.round(Math.max(zone.bounds.width, zone.bounds.height) * s);
      const pageBudget = Math.round(viewport / 2);
      const leftCanvas = await this._scaleToBudget(leftImg, pageBudget);
      const rightCanvas = await this._scaleToBudget(rightImg, pageBudget);

      const renderOpts = this._webglAvailable
        ? {
            width: viewport,
            height: viewport,
            binding: transform.binding ?? 'saddle',
            pages: transform.pages ?? 60,
            curveIntensity: transform.curveIntensity ?? 0.15,
            cameraTiltX: transform.cameraTiltX ?? 45,
            cameraRotateY: transform.cameraRotateY ?? 0,
            cameraRotateZ: transform.cameraRotateZ ?? 0,
            aspectRatio: transform.aspectRatio ?? null,
            zoom: transform.zoom ?? 1,
            panX: transform.panX ?? 0,
            panY: transform.panY ?? 0,
            roughness: transform.roughness ?? 0.3,
            shadowStrength: transform.shadowStrength ?? 0.25,
          }
        : {
            width: viewport,
            height: viewport,
            pageCurve: true,
            curveIntensity: transform.curveIntensity ?? 0.15,
            spineShadow: true,
            dropShadow: true,
          };

      const renderedCanvas = this.spreadRenderer.render(leftCanvas, rightCanvas, renderOpts);

      // Contain-fit the square render into the bounds box, centered.
      const fabricImg = this._placeRenderInBounds(renderedCanvas, zone, viewport);

      // Apply perspective/skew if defined
      if (transform.perspective) {
        const p = transform.perspective;
        if (p.skewX) fabricImg.set('skewX', p.skewX);
        if (p.skewY) fabricImg.set('skewY', p.skewY);
      }

      // Apply rotation if defined
      if (transform.rotation) {
        fabricImg.set('angle', transform.rotation);
      }

      fabricImg.set('zoneId', zone.id);
      fabricImg.set('zoneType', zone.type);
      return fabricImg;
    } catch (err) {
      console.error('Failed to render spread:', err);
      return this._createPlaceholder(zone, 'Spread');
    }
  }

  /**
   * Create a badge zone (edition label).
   */
  _createBadge(zone, variables) {
    const text = this._resolveBinding(zone.binding, variables) || zone.label || 'Badge';
    const style = zone.style || {};

    const bgColor = style.backgroundColor || '#1d76ba';
    const textColor = style.textColor || '#FFFFFF';
    const radius = style.borderRadius || 8;
    const fontSize = style.fontSize || 24;
    const fontWeight = style.fontWeight || 700;
    const paddingX = style.paddingX || 20;
    const paddingY = style.paddingY || 8;

    // Create background rectangle
    const bgRect = new fabric.Rect({
      width: zone.bounds.width,
      height: zone.bounds.height,
      rx: radius,
      ry: radius,
      fill: bgColor,
      originX: 'left',
      originY: 'top',
    });

    // Create text
    const badgeText = new fabric.Text(text, {
      fontFamily: style.fontFamily || 'Helvetica',
      fontSize: fontSize,
      fontWeight: fontWeight,
      fill: textColor,
      originX: 'center',
      originY: 'center',
      left: zone.bounds.width / 2,
      top: zone.bounds.height / 2,
    });

    // Use superscript for 'th', 'st', 'nd', 'rd' by checking and adjusting
    const group = new fabric.Group([bgRect, badgeText], {
      left: zone.bounds.x,
      top: zone.bounds.y,
      originX: 'left',
      originY: 'top',
      selectable: true,
      evented: true,
    });

    group.set('zoneId', zone.id);
    group.set('zoneType', zone.type);
    return group;
  }

  /**
   * Create a CTA (Call to Action) zone with text and icons.
   */
  async _createCTA(zone, variables) {
    const style = zone.style || {};
    const bgColor = style.backgroundColor || '#1d76ba';
    const textColor = style.textColor || '#FFFFFF';
    const radius = style.borderRadius || 6;
    const fontSize = style.fontSize || 36;
    const fontWeight = style.fontWeight || 900;
    const borderWidth = parseInt(style.border?.split('px')[0]) || 3;
    const borderColor = style.border?.includes('#') ? style.border.split(' ').pop() : '#FFFFFF';

    const ctaText = this._resolveBinding(zone.binding, variables) || 'FREE DOWNLOAD';

    // Background with border
    const bgRect = new fabric.Rect({
      width: zone.bounds.width,
      height: zone.bounds.height,
      rx: radius,
      ry: radius,
      fill: bgColor,
      stroke: borderColor,
      strokeWidth: borderWidth,
      originX: 'left',
      originY: 'top',
    });

    const objects = [bgRect];

    // Calculate text position — leave room for icons on the right
    const hasIcons = zone.icons && zone.icons.length > 0;
    const iconSpace = hasIcons ? 80 : 0;
    const textLeft = (zone.bounds.width - iconSpace) / 2;

    const text = new fabric.Text(ctaText, {
      fontFamily: style.fontFamily || 'Helvetica',
      fontSize: fontSize,
      fontWeight: fontWeight,
      fill: textColor,
      originX: 'center',
      originY: 'center',
      left: textLeft,
      top: zone.bounds.height / 2,
    });
    objects.push(text);

    // Load icons if specified
    if (hasIcons) {
      const iconSize = Math.min(zone.bounds.height - 16, 40);
      let iconX = zone.bounds.width - 16;

      for (let i = zone.icons.length - 1; i >= 0; i--) {
        const iconName = zone.icons[i];
        try {
          const iconUrl = `/assets/icons/${iconName}-icon.svg`;
          const iconObj = await fabric.FabricImage.fromURL(iconUrl);
          const scale = iconSize / Math.max(iconObj.width, iconObj.height);
          iconObj.set({
            scaleX: scale,
            scaleY: scale,
            originX: 'right',
            originY: 'center',
            left: iconX,
            top: zone.bounds.height / 2,
          });
          objects.push(iconObj);
          iconX -= iconSize + 8;
        } catch (err) {
          console.warn(`Failed to load icon: ${iconName}`, err);
        }
      }
    }

    const group = new fabric.Group(objects, {
      left: zone.bounds.x,
      top: zone.bounds.y,
      originX: 'left',
      originY: 'top',
      selectable: true,
      evented: true,
    });

    group.set('zoneId', zone.id);
    group.set('zoneType', zone.type);
    return group;
  }

  /**
   * Create a simple image zone.
   */
  async _createImage(zone, variables) {
    const src = this._resolveBinding(zone.binding, variables);
    if (!src) return this._createPlaceholder(zone, 'Image');

    try {
      const img = typeof src === 'string' ? await loadImage(src) : src;
      const fabricImg = new fabric.FabricImage(img, {
        left: zone.bounds.x,
        top: zone.bounds.y,
        originX: 'left',
        originY: 'top',
        selectable: false,
        evented: false,
      });

      // Scale to fit bounds
      const scaleX = zone.bounds.width / img.width;
      const scaleY = zone.bounds.height / img.height;
      const scale = Math.min(scaleX, scaleY);
      fabricImg.set({ scaleX: scale, scaleY: scale });

      fabricImg.set('zoneId', zone.id);
      fabricImg.set('zoneType', zone.type);
      return fabricImg;
    } catch (err) {
      console.error('Failed to load image:', err);
      return this._createPlaceholder(zone, 'Image');
    }
  }

  /**
   * Create a rectangle zone.
   */
  _createRect(zone, variables) {
    const style = zone.style || {};
    const rect = new fabric.Rect({
      left: zone.bounds.x,
      top: zone.bounds.y,
      width: zone.bounds.width,
      height: zone.bounds.height,
      originX: 'left',
      originY: 'top',
      fill: style.fill || 'transparent',
      stroke: style.stroke || 'transparent',
      strokeWidth: style.strokeWidth || 0,
      rx: style.borderRadius || 0,
      ry: style.borderRadius || 0,
      opacity: style.opacity || 1,
      selectable: true,
      evented: true,
    });

    rect.set('zoneId', zone.id);
    rect.set('zoneType', zone.type);
    return rect;
  }

  /**
   * Create a placeholder rectangle for zones with missing data.
   */
  _createPlaceholder(zone, label) {
    const rect = new fabric.Rect({
      width: zone.bounds.width,
      height: zone.bounds.height,
      fill: 'rgba(255,255,255,0.05)',
      stroke: 'rgba(255,255,255,0.2)',
      strokeWidth: 1,
      strokeDashArray: [6, 4],
      rx: 4,
      ry: 4,
      originX: 'left',
      originY: 'top',
    });

    const text = new fabric.Text(label, {
      fontSize: 14,
      fontFamily: 'Nunito',
      fontWeight: 600,
      fill: 'rgba(255,255,255,0.3)',
      originX: 'center',
      originY: 'center',
      left: zone.bounds.width / 2,
      top: zone.bounds.height / 2,
    });

    const group = new fabric.Group([rect, text], {
      left: zone.bounds.x,
      top: zone.bounds.y,
      originX: 'left',
      originY: 'top',
      selectable: true,
      evented: true,
    });

    group.set('zoneId', zone.id);
    group.set('zoneType', zone.type);
    return group;
  }

  /**
   * Place a square 3D render canvas into a zone's bounds box, centered.
   *
   * The render viewport is square and the magazine is auto-framed within it,
   * so we scale the square to the bounds' larger dimension and centre it. Any
   * overflow is transparent, so the magazine keeps correct proportions and a
   * prominent size regardless of the bounds aspect ratio. Stores metadata used
   * by the live property-editing path in main.js.
   *
   * @param {HTMLCanvasElement} renderedCanvas Square render (viewport×viewport px)
   * @param {object} zone
   * @param {number} viewport Square render size in px
   * @returns {fabric.FabricImage}
   */
  _placeRenderInBounds(renderedCanvas, zone, viewport) {
    const b = zone.bounds;
    const placedSize = Math.max(b.width, b.height);
    const displayScale = placedSize / viewport;
    const left = b.x + (b.width - placedSize) / 2;
    const top = b.y + (b.height - placedSize) / 2;

    const fabricImg = new fabric.FabricImage(renderedCanvas, {
      left,
      top,
      originX: 'left',
      originY: 'top',
      scaleX: displayScale,
      scaleY: displayScale,
      selectable: true,
      evented: true,
      // Click+drag moves the magazine (like text/badge zones).
      // Shift+drag or double-click enters 3D orbit mode (handled in main.js).
      hoverCursor: 'move',
    });
    fabricImg.set('zoneBounds', { ...b });
    fabricImg.set('zoneDisplayScale', displayScale);
    return fabricImg;
  }

  /**
   * Scale an image/canvas source preserving its natural aspect ratio so that
   * its longest side equals `budget` px. Returns an HTMLCanvasElement.
   *
   * This replaces the previous behaviour of force-resizing the source to the
   * zone bounds ratio, which non-uniformly squashed portrait magazine covers
   * and pages into landscape boxes. The 3D renderers derive their geometry
   * aspect ratio from the canvas dimensions, so preserving aspect here fixes
   * the distortion end-to-end.
   *
   * @param {HTMLImageElement|HTMLCanvasElement|ImageBitmap} img
   * @param {number} budget Target longest-side length in px
   * @returns {Promise<HTMLCanvasElement>}
   */
  async _scaleToBudget(img, budget) {
    const srcW = img.naturalWidth || img.width;
    const srcH = img.naturalHeight || img.height;
    const scale = budget / Math.max(srcW, srcH);
    const w = Math.max(1, Math.round(srcW * scale));
    const h = Math.max(1, Math.round(srcH * scale));
    const bmp = await createImageBitmap(img, {
      resizeWidth: w,
      resizeHeight: h,
      resizeQuality: 'high',
    });
    const canvas = this._imageBitmapToCanvas(bmp);
    bmp.close?.();
    return canvas;
  }

  /**
   * Convert an ImageBitmap to an HTMLCanvasElement.
   * Three.js CanvasTexture respects flipY for canvas sources but not for
   * ImageBitmap, so we must convert to canvas for correct texture orientation.
   */
  _imageBitmapToCanvas(bitmap) {
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(bitmap, 0, 0);
    return canvas;
  }

  /**
   * Resolve a variable binding path.
   * Supports simple keys and array notation: "spreadPages[0]"
   */
  _resolveBinding(binding, variables) {
    if (!binding || !variables) return null;

    // Handle array indexing: "spreadPages[0]"
    const arrayMatch = binding.match(/^(\w+)\[(\d+)\]$/);
    if (arrayMatch) {
      const arr = variables[arrayMatch[1]];
      const idx = parseInt(arrayMatch[2]);
      return Array.isArray(arr) ? arr[idx] : null;
    }

    return variables[binding] ?? null;
  }

  /**
   * Parse a CSS shadow string (e.g. "3px 3px 10px rgba(0,0,0,0.6)")
   * into a fabric.Shadow instance.
   * @param {string} str
   * @returns {fabric.Shadow}
   */
  _parseShadowString(str) {
    const parts = str.trim().split(/\s+/);
    let offsetX = 0, offsetY = 0, blur = 0;
    let color = 'rgba(0,0,0,0.5)';
    const colorParts = [];

    for (const part of parts) {
      if (part.endsWith('px')) {
        const num = parseFloat(part);
        if (offsetX === 0 && offsetY === 0 && blur === 0) {
          offsetX = num;
        } else if (offsetY === 0 && blur === 0) {
          offsetY = num;
        } else {
          blur = num;
        }
      } else {
        colorParts.push(part);
      }
    }
    if (colorParts.length > 0) color = colorParts.join(' ');

    return new fabric.Shadow({ color, blur, offsetX, offsetY });
  }
}
